import { isValid, isDirty, submit } from 'redux-form'
import { Modal } from '../../components/common'
import { openModal } from '../../actions/app'
import {
  SELECT_SPECIAL_DIGITAL_FORMATS_FORM,
  UPDATE_TYPE_DIGITAL_ACCOMMODATIONS,
  SDF_MULTIDAY,
} from '../../constants/StudentConstants'
import { DIGITAL_EXAM } from '../../constants/SettingsConstants'
import SelectSpecialDigitalFormatsToBeOrderedForm from '../forms/SelectSpecialDigitalFormatsToBeOrderedForm'
import { resetStudentUpdate } from '../../actions/studentsCommon'

const mapStateToProps = (state, { examId }) => {
  const {
    studentsByOrg: {
      update: { updating, updated, error },
      courseMap,
      studentMap,
      exams,
    },
  } = state
  const exam = exams.find(e => e.examId === examId)
  const { lastName, firstName, orderChanged } = studentMap?.[exam.studentId] ?? {}
  const { specialDigitalFormats = {} } = exam

  return {
    isDigital: exam.examFormat === DIGITAL_EXAM,
    updating,
    updated,
    error,
    valid: isValid(SELECT_SPECIAL_DIGITAL_FORMATS_FORM)(state),
    dirty: isDirty(SELECT_SPECIAL_DIGITAL_FORMATS_FORM)(state),
    exam,
    courseName: courseMap[exam.testCd].name,
    studentName: `${firstName} ${lastName}`,
    orderChanged,
    hasMultidayTesting: specialDigitalFormats[SDF_MULTIDAY] !== undefined,
  }
}

function footerActions({ valid, updating, dirty, submitForm }) {
  return [
    {
      buttonLabel: 'Cancel',
      isDismissable: true,
      isPrimary: false,
      isDisabled: updating,
    },
    {
      buttonLabel: 'Update',
      isPrimary: true,
      onClick: submitForm,
      isDisabled: !valid || updating || !dirty,
      busy: updating,
    },
  ]
}

const OrderSpecialDigitalFormatsModal = ({
  isDigital,
  updating,
  updated,
  valid,
  dirty,
  exam,
  courseName,
  studentName,
  orderChanged,
  modalCloseFocusElem,
  submit,
  resetStudentUpdate,
  openModal,
}) => {
  const [shouldCloseModal, setShouldCloseModal] = useState(false)
  const [onCloseAction, setOnCloseAction] = useState(null)

  useEffect(() => {
    if (updated) {
      if (orderChanged) {
        setOnCloseAction(() => {
          openModal('UpdateStudentExamSuccessModal', {
            exam,
            type: UPDATE_TYPE_DIGITAL_ACCOMMODATIONS,
            courseName,
            studentName,
            modalCloseFocusElem,
          })
        })
      }
      setShouldCloseModal(true)
    }
    return () => resetStudentUpdate()
  }, [updated, orderChanged, exam, courseName, studentName, modalCloseFocusElem])

  return (
    <Modal
      shouldCloseModal={shouldCloseModal}
      onCloseAction={onCloseAction}
      modalCloseFocusElem={modalCloseFocusElem}
      headerTitle={`${courseName} ${
        isDigital ? 'Digital' : 'Hybrid'
      } Exam: Special Format for Students with Accommodations`}
      footerActions={footerActions({
        valid,
        updating,
        dirty,
        submitForm: () => submit(SELECT_SPECIAL_DIGITAL_FORMATS_FORM),
      })}
    >
      {isDigital ? (
        <>
          <p>
            This student will receive a format of the digital exam with the accommodations listed
            below. Confirm that the accommodations listed are accurate for this exam. If the student
            wants to waive any of these accommodations for this digital exam, you&#39;ll need to
            de-select those accommodations here and then click <strong>Update</strong> at the bottom
            of the screen.
          </p>
          <p>
            If the accommodations listed below are accurate and the student doesn&#39;t want to make
            any changes, no further action is needed.
          </p>
          <p>
            <strong>Note:</strong> As with paper-and-pencil exams, some types of accommodations
            require students to take the digital exam in separate rooms from other test-takers.
            Refer to the <em>AP Accommodations Guide</em> for details about accommodations that
            require separate testing rooms.
          </p>
        </>
      ) : (
        <>
          <p>
            This student will receive a format of the hybrid exam with the accommodations listed
            below. Confirm that the accommodations listed are accurate for this exam. If the student
            wants to waive any of these accommodations for this exam, you&#39;ll need to de-select
            those accommodations here and then click <strong>Update</strong> at the bottom of the
            screen.
          </p>
          <p>
            If the accommodations listed below are accurate and the student doesn&#39;t want to make
            any changes, no further action is needed.
          </p>
          <p>
            <strong>Note:</strong> As with paper-and-pencil exams, some types of accommodations
            require students to take the exam in separate rooms from other test-takers. Refer to the{' '}
            <em>AP Accommodations Guide</em> for details about accommodations that require separate
            testing rooms.
          </p>
        </>
      )}
      <SelectSpecialDigitalFormatsToBeOrderedForm exam={exam} />
    </Modal>
  )
}

export default connect(mapStateToProps, {
  submit,
  openModal,
  resetStudentUpdate,
})(OrderSpecialDigitalFormatsModal)
